import { useState } from "react";
import { motion } from "framer-motion";
import { Github, Instagram, Mail } from "lucide-react";

const SUMMARY = `Creative technologist with 15+ years of experience working across visual production, software engineering, and multimedia systems. I move projects from concept to production-ready outcomes without fragmentation between creative and technical layers — handling design, development, deployment, and everything in between.`;

const capabilities = [
  {
    title: "Visual Production & Digital Art",
    summary: "Production-level image compositing, color grading, motion graphics, and AI-assisted asset refinement. Delivers polished visual assets and identity systems for branding, restoration, and visual storytelling.",
    tools: ["Photoshop", "Premiere", "After Effects", "DaVinci Resolve"],
  },
  {
    title: "3D, CAD & Technical Design",
    summary: "End-to-end 3D asset creation from concept to production — sculpting, hard-surface modeling, CAD prototyping, and print-ready optimization for both visual and functional use.",
    tools: ["Blender", "Fusion 360", "Cura"],
  },
  {
    title: "Software Development",
    summary: "Full-stack web applications built with modern frameworks and BaaS platforms. Handles authentication, data management, API integration, and cloud deployment from a single, maintainable codebase.",
    tools: ["React", "Next.js", "TypeScript", "Firebase", "Supabase"],
  },
  {
    title: "Game & Interactive Development",
    summary: "Gameplay systems, state management, and real-time interactive experiences. Builds functional prototypes and production-ready applications with synchronized audio, visuals, and interaction.",
    tools: ["Godot", "Android Studio"],
  },
  {
    title: "Audio & Multimedia Production",
    summary: "Sound design, audio editing, and mixing for games, video, and interactive media. Delivers clean, integrated audio that fits seamlessly into larger cross-media productions.",
    tools: ["Cubase", "Reaper"],
  },
  {
    title: "Automation & Technical Pipelines",
    summary: "Custom tools and scripts that eliminate repetitive work across creative and technical pipelines. Automates deployment, asset handling, environment configuration, and cross-media workflows.",
    tools: ["Git", "SSH", "DNS", "Hosting"],
  },
];

const strengths = [
  "End-to-end ownership of multidisciplinary projects",
  "Bridges creative direction with technical execution",
  "Spans visual, audio, software, and interactive media",
  "Systems thinking applied to production pipelines",
  "Fast, self-directed adoption of new tools and workflows",
];

const techStack = [
  { category: "Frontend", items: ["React", "Next.js", "TypeScript", "Vite"] },
  { category: "Backend", items: ["Firebase", "Supabase", "SQL", "NoSQL"] },
  { category: "Creative", items: ["Photoshop", "Premiere", "After Effects", "DaVinci Resolve"] },
  { category: "3D", items: ["Blender", "Fusion 360"] },
  { category: "Audio", items: ["Cubase", "Reaper"] },
  { category: "Interactive", items: ["Godot", "Android Studio"] },
  { category: "Infra", items: ["Git", "SSH", "DNS", "Hosting"] },
];

const DISCORD_TAG = "DheLiriouS#9452";

const icon = "w-5 h-5";

const discordIcon = (
  <svg className={icon} viewBox="0 0 640 640" fill="currentColor">
    <path d="M524.5 133.8C524.3 133.5 524.1 133.2 523.7 133.1C485.6 115.6 445.3 103.1 404 96C403.6 95.9 403.2 96 402.9 96.1C402.6 96.2 402.3 96.5 402.1 96.9C396.6 106.8 391.6 117.1 387.2 127.5C342.6 120.7 297.3 120.7 252.8 127.5C248.3 117 243.3 106.8 237.7 96.9C237.5 96.6 237.2 96.3 236.9 96.1C236.6 95.9 236.2 95.9 235.8 95.9C194.5 103 154.2 115.5 116.1 133C115.8 133.1 115.5 133.4 115.3 133.7C39.1 247.5 18.2 358.6 28.4 468.2C28.4 468.5 28.5 468.7 28.6 469C28.7 469.3 28.9 469.4 29.1 469.6C73.5 502.5 123.1 527.6 175.9 543.8C176.3 543.9 176.7 543.9 177 543.8C177.3 543.7 177.7 543.4 177.9 543.1C189.2 527.7 199.3 511.3 207.9 494.3C208 494.1 208.1 493.8 208.1 493.5C208.1 493.2 208.1 493 208 492.7C207.9 492.4 207.8 492.2 207.6 492.1C207.4 492 207.2 491.8 206.9 491.7C191.1 485.6 175.7 478.3 161 469.8C160.7 469.6 160.5 469.4 160.3 469.2C160.1 469 160 468.6 160 468.3C160 468 160 467.7 160.2 467.4C160.4 467.1 160.5 466.9 160.8 466.7C163.9 464.4 167 462 169.9 459.6C170.2 459.4 170.5 459.2 170.8 459.2C171.1 459.2 171.5 459.2 171.8 459.3C268 503.2 372.2 503.2 467.3 459.3C467.6 459.2 468 459.1 468.3 459.1C468.6 459.1 469 459.3 469.2 459.5C472.1 461.9 475.2 464.4 478.3 466.7C478.5 466.9 478.7 467.1 478.9 467.4C479.1 467.7 479.1 468 479.1 468.3C479.1 468.6 479 468.9 478.8 469.2C478.6 469.5 478.4 469.7 478.2 469.8C463.5 478.4 448.2 485.7 432.3 491.6C432.1 491.7 431.8 491.8 431.6 492C431.4 492.2 431.3 492.4 431.2 492.7C431.1 493 431.1 493.2 431.1 493.5C431.1 493.8 431.2 494 431.3 494.3C440.1 511.3 450.1 527.6 461.3 543.1C461.5 543.4 461.9 543.7 462.2 543.8C462.5 543.9 463 543.9 463.3 543.8C516.2 527.6 565.9 502.5 610.4 469.6C610.6 469.4 610.8 469.2 610.9 469C611 468.8 611.1 468.5 611.1 468.2C623.4 341.4 590.6 231.3 524.2 133.7zM222.5 401.5C193.5 401.5 169.7 374.9 169.7 342.3C169.7 309.7 193.1 283.1 222.5 283.1C252.2 283.1 275.8 309.9 275.3 342.3C275.3 375 251.9 401.5 222.5 401.5zM417.9 401.5C388.9 401.5 365.1 374.9 365.1 342.3C365.1 309.7 388.5 283.1 417.9 283.1C447.6 283.1 471.2 309.9 470.7 342.3C470.7 375 447.5 401.5 417.9 401.5z"/>
  </svg>
);

const behanceIcon = (
  <svg className={icon} viewBox="0 0 640 640" fill="currentColor">
    <path d="M264 301.2C295.8 286 312.4 263 312.4 227.2C312.4 156.6 259.8 139.4 199.1 139.4L32 139.4L32 493.8L203.8 493.8C268.2 493.8 328.7 462.9 328.7 390.9C328.7 346.4 307.6 313.5 264 301.2zM109.9 199.9L183 199.9C211.1 199.9 236.4 207.8 236.4 240.4C236.4 270.5 216.7 282.6 188.9 282.6L109.9 282.6L109.9 199.9zM193.2 433.6L109.9 433.6L109.9 336L194.8 336C229.1 336 250.8 350.3 250.8 386.6C250.8 422.4 224.9 433.6 193.2 433.6zM551.7 192.9L408 192.9L408 158L551.7 158L551.7 192.9zM608 369.2C608 293.3 563.6 230 483.1 230C404.9 230 351.8 288.8 351.8 365.8C351.8 445.7 402.1 500.5 483.1 500.5C544.4 500.5 584.1 472.9 603.2 414.2L541 414.2C534.3 436.1 506.7 447.7 485.3 447.7C444 447.7 422.3 423.5 422.3 382.4L607.4 382.4C607.7 378.2 608 373.7 608 369.2zM422.4 338C424.7 304.3 447.1 283.2 480.9 283.2C516.3 283.2 534.1 304 537.1 338L422.4 338z"/>
  </svg>
);

const redditIcon = (
  <svg className={icon} viewBox="0 0 640 640" fill="currentColor">
    <path d="M437 202.6C411.8 202.6 390.7 185.1 385.1 161.6C354.5 165.9 330.9 192.3 330.9 224L330.9 224.2C378.3 226 421.5 239.3 455.8 260.5C468.4 250.8 484.2 245 501.3 245C542.6 245 576 278.4 576 319.7C576 349.5 558.6 375.2 533.3 387.2C530.9 474 436.3 543.8 320.1 543.8C203.9 543.8 109.5 474.1 107 387.4C81.6 375.5 64 349.7 64 319.7C64 278.4 97.4 245 138.7 245C155.9 245 171.7 250.8 184.4 260.6C218.4 239.5 261.2 226.2 308.1 224.2L308.1 223.9C308.1 179.6 341.8 143 384.9 138.4C389.8 114.2 411.2 96 437 96C466.4 96 490.3 119.9 490.3 149.3C490.3 178.7 466.4 202.6 437 202.6zM221.5 319.3C200.6 319.3 182.6 340.1 181.3 367.2C180 394.3 198.4 405.3 219.3 405.3C240.2 405.3 255.9 395.5 257.1 368.4C258.3 341.3 242.4 319.3 221.4 319.3L221.5 319.3zM459 367.1C457.8 340 439.8 319.2 418.8 319.2C397.8 319.2 381.9 341.2 383.1 368.3C384.3 395.4 400 405.2 420.9 405.2C441.8 405.2 460.2 394.2 458.9 367.1L459 367.1zM398.9 437.9C400.4 434.3 397.9 430.2 394 429.8C371 427.5 346.1 426.2 320.2 426.2C294.3 426.2 269.4 427.5 246.4 429.8C242.5 430.2 240 434.3 241.5 437.9C254.4 468.7 284.8 490.3 320.2 490.3C355.6 490.3 386 468.7 398.9 437.9z"/>
  </svg>
);

const telegramIcon = (
  <svg className={icon} viewBox="0 0 512 512" fill="currentColor">
    <path d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm147.3 188.7l-43.6 205.3c-3.2 15.1-12.1 18.9-24.7 11.7l-68.1-50.3-33.4 32.5c-3.7 3.7-6.8 6.8-14.1 6.8l5.2-73.8 132.1-119.2c5.9-5.3-1.3-8.2-10.6-3.7L220 341.4l-82.5-26.8c-16-5.2-16.3-16 3.7-23.6l322-124.4c13.3-5 24.9 3.3 20.1 23.1z"/>
  </svg>
);

const socialLinks = [
  { href: "https://www.github.com/DheliriouS", icon: <Github className={icon} /> },
  { href: "https://www.behance.net/DheliriouS", icon: behanceIcon },
  { href: "https://www.reddit.com/user/DheliriouS", icon: redditIcon },
  { href: "https://www.instagram.com/DheliriouS.ArtworkS", icon: <Instagram className={icon} /> },
  { href: "mailto:dhelirious.artworks@gmail.com", icon: <Mail className={icon} /> },
  { href: "", icon: discordIcon, isCopy: true },
  { href: "https://t.me/JuFore", icon: telegramIcon },
];

function CapabilityCard({ cap, index }: { cap: typeof capabilities[number]; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.08, ease: "easeOut" }}
      className="group bg-white/[0.03] border border-white/[0.06] rounded-lg p-5 text-left hover:bg-white/[0.06] hover:border-white/[0.12] transition-all duration-500"
    >
      <h4 className="text-xs font-light tracking-widest text-white/90 uppercase mb-3">
        {cap.title}
      </h4>
      <p className="text-sm text-white/65 font-light leading-relaxed mb-4">
        {cap.summary}
      </p>
      <div className="flex flex-wrap gap-1.5 opacity-70 group-hover:opacity-100 transition-opacity duration-500">
        {cap.tools.map((tool) => (
          <span
            key={tool}
            className="text-xs px-2 py-0.5 rounded-full bg-white/[0.12] text-white/55"
          >
            {tool}
          </span>
        ))}
      </div>
    </motion.div>
  );
}

export function AboutSection() {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.select();
      const success = document.execCommand("copy");
      document.body.removeChild(textArea);
      return success;
    } catch {
      return false;
    }
  };

  const handleCopyDiscord = async () => {
    const success = await copyToClipboard(DISCORD_TAG);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <motion.section
      id="about-section"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="w-full max-w-4xl mx-auto px-6 mt-16 pb-12"
    >
      <div className="border-t border-white/20 pt-6 text-center space-y-6">
        <h2 className="text-sm md:text-base font-light tracking-widest text-white/90 uppercase">
          ABOUT
        </h2>

        <div className="text-sm md:text-base leading-relaxed text-white/60 font-light text-center max-w-3xl mx-auto">
          <p>{SUMMARY}</p>
        </div>
      </div>

      <div className="mt-10">
        <h3 className="text-xs font-light tracking-widest text-white/90 uppercase text-center mb-6">
          Capabilities
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {capabilities.map((cap, i) => (
            <CapabilityCard key={cap.title} cap={cap} index={i} />
          ))}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="mt-10 text-center"
      >
        <h3 className="text-xs font-light tracking-widest text-white/90 uppercase mb-4">
          Cross-Disciplinary Strengths
        </h3>
        <div className="flex flex-wrap justify-center gap-2">
          {strengths.map((s) => (
            <span
              key={s}
              className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-white/50 hover:border-white/20 hover:text-white/60 transition-all duration-300"
            >
              {s}
            </span>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="mt-10 text-center"
      >
        <h3 className="text-xs font-light tracking-widest text-white/90 uppercase mb-4">
          Technical Stack
        </h3>
        <div className="flex flex-wrap justify-center gap-1.5">
          {techStack.map((group) =>
            group.items.map((item) => (
              <span
                key={item}
                className="text-xs px-3 py-1 rounded-full bg-white/10 text-white/50"
              >
                {item}
              </span>
            ))
          )}
        </div>
        <p className="text-[11px] text-white/30 mt-3 font-light tracking-wide">
          {techStack.map((g) => g.category).join(" \u00B7 ")}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="flex justify-center gap-3 pt-10"
      >
        {socialLinks.map((link, i) =>
          link.isCopy ? (
            <button
              key={i}
              onClick={handleCopyDiscord}
              className="relative text-white/40 hover:text-white/70 transition-colors"
              title="Copy Discord tag"
            >
              {link.icon}
              {copied && (
                <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs whitespace-nowrap bg-white/10 px-2 py-1 rounded">
                  Copied!
                </span>
              )}
            </button>
          ) : (
            <a
              key={i}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/40 hover:text-white/70 transition-colors"
            >
              {link.icon}
            </a>
          )
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="flex justify-center pt-8"
      >
        <div className="relative w-screen left-1/2 -translate-x-1/2 md:px-6">
          <div className="relative w-full aspect-video">
            <iframe
              src="https://www.youtube.com/embed/mz8onAwoS8U"
              title="DheLiriouS"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full rounded-lg"
            />
          </div>
        </div>
      </motion.div>
    </motion.section>
  );
}
